import { Button, Tooltip } from '@mantine/core';
import Link from 'next/link';
import { notifications } from '@mantine/notifications';
import { IconExclamationCircle, IconDeviceFloppy, IconStrikethrough } from '@tabler/icons-react';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';

export default function SkipTestInSuiteRunButton({
  testId,
  testSuiteId,
  suiteRunId,
}: {
  testId: string;
  testSuiteId: string;
  suiteRunId?: string;
}) {
  const projectId = useProjectId();
  const trpcUtils = api.useUtils();

  const { mutateAsync: updateTestMembership, isPending } = api.testSuite.updateTests.useMutation({
    onSuccess: async () => {
      notifications.show({
        color: 'teal',
        message: (
          <div>
            Test skipped. You can unskip it in the{' '}
            <Link
              href={`/project/${projectId}/testSuite/${testSuiteId}`}
              className="text-teal-600 hover:underline"
            >
              test suite page
            </Link>
            .
          </div>
        ),
        icon: <IconDeviceFloppy />,
      });
      await Promise.allSettled([
        trpcUtils.testSuite.list.invalidate(),
        trpcUtils.testSuite.get.invalidate(),
        trpcUtils.test.listTests.invalidate(),
        trpcUtils.suiteRun.get.invalidate({ runId: suiteRunId }),
      ]);
    },
    onError: (e) => {
      console.error(e);
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `${e}`,
        icon: <IconExclamationCircle />,
      });
    },
  });

  return (
    <div className="w-4">
      <Tooltip
        label="This test is failing often. Click this button to skip it during future runs."
        w={250}
        multiline
        withArrow
      >
        <Button
          variant="light"
          onClick={async () => {
            await updateTestMembership({
              testSuiteId,
              projectId: projectId || '',
              operation: 'skip',
              testIds: [testId],
            });
          }}
          loading={isPending}
          leftSection={<IconStrikethrough size={16} />}
        >
          Skip
        </Button>
      </Tooltip>
    </div>
  );
}
